import moment from 'moment';
import _get from 'lodash/get';
import _range from 'lodash/range';
import _find from 'lodash/find';

import qs from 'qs';

import { handleActions } from 'redux-actions';
import { getFormValues, change } from 'redux-form';
import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';
import http from '../shared/http';
import createFilters from '../shared/fixed-filters';
import endpoints from '../../config/endpoints';
import { default as createCalendar } from '../shared/calendar';

import { regions } from '../../../../../enums/regions';
import { LOCATION_CHANGE } from 'react-router-redux';

// actions types
const FETCH_DATA_SUCCESS = 'top-countries-list/fetch-data-success';
const FETCH_DATA_ERROR = 'top-countries-list/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'top-countries-list/toggle-progress-bar';
const TOGGLE_NOTE_POPUP = 'top-countries-list/TOGGLE_NOTE_POPUP';

const PAGINATE = 'top-countries-list/PAGINATE';
const DEFAULT_PAGINATION = 'top-countries-list/DEFAULT_PAGINATION';
const TOGGLE_PERIOD_SWITCH = 'top-countries-list/TOGGLE_PERIOD_SWITCH';

const FETCH_EXTENDED_DATA_SUCCESS = 'top-countries-list/FETCH_EXTENDED_DATA_SUCCESS';
const FETCH_NOTES_DATA_SUCCESS = 'top-countries-list/FETCH_NOTES_DATA_SUCCESS';
const REMOVE_EXTENDED_DATA = 'top-countries-list/REMOVE_EXTENDED_DATA';
const REMOVE_EXTENDED_OFFER_DATA = 'top-countries-list/REMOVE_EXTENDED_OFFER_DATA';
const SET_LOADING_COUNTRY_ID = 'top-countries-list/SET_LOADING_COUNTRY_ID';
const SET_LOADING_OFFER_COUNTRY_ID = 'top-countries-list/SET_LOADING_OFFER_COUNTRY_ID';

const FETCH_AFFILIATES_EXTENDED_DATA_SUCCESS = 'top-countries-list/FETCH_AFFILIATES_EXTENDED_DATA_SUCCESS';
const START_LOAD_AFFILIATE_DATA = 'top-countries-list/START_LOAD_AFFILIATE_DATA';

// permissions
export const allowedPermissions = {
	viewAll: 'top_countries_view_all',
	viewProfit: 'top_countries_view_profits',
	view: 'affiliates_list'
};

const calendar = createCalendar({
	namespace: 'topCountriesList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'topCountriesList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_IDS
			}
		},
		{
			name: 'admins',
			title: 'Manager name',
			placeholder: 'Manager name',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100&is_show_name=true'
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			searchable: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'traffic_source',
			title: 'Traffic source',
			placeholder: 'Select traffic source',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.traffic-sources'
			}
		},
		{
			name: 'offer_geo',
			title: 'Offer geo',
			placeholder: 'Add offer geo',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_GEO
			}
		}
	]
});

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	periodSwitch: null,
	loadingAffiliateId: null,
	extendedOfferData: [],
	extendedAffiliateData: [],
	expandedCountryIds: [],
	expandedCountryOfferIds: [],
	expandedOfferIds: [],
	noteData: null,
	notePopupData: null,
	isNotePopupVisible: false,
	table: {
		columns: {
			country_id: {
				index: 'country_id',
				title: 'Country',
				className: 'fixed-col bald-font text-center'
			},
			forecast: {
				index: 'forecast',
				title: 'Forecast',
				className: 'text-center'
			}
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	filters: filters.initialState,
	calendar: calendar.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const {
				filters: { selectedFilters }, pagination, expandedCountryIds, periodSwitch
			} = getState().topCountriesList;
			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;
			const leadsFrom = getFormValues('conversionsFromTopCountriesForm')(getState()) || {};

			const query = qs.stringify({
				filters,
				period: periodSwitch,
				limit: pagination.perPage,
				offset: pagination.perPage * (pagination.currentPage - 1),
				leads_from: leadsFrom.conversionsFrom,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});
			dispatch({ type: TOGGLE_PROGRESS_BAR });

			return (http({
				url: `v2/top-countries?${query}`,
				method: 'get',
				actions: [ FETCH_DATA_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch)
				.then(async action => {
					Object.keys(leadsFrom)
						.forEach(key => dispatch(change('conversionsFromTopCountriesForm', key, leadsFrom[key])));

					if (action.type === FETCH_DATA_SUCCESS && expandedCountryIds[0]) {
						return dispatch(actions.fetchExtendedOfferData());
					}
				});
		},
		fetchExtendedOfferData: (country_id) => (dispatch, getState) => {
			const {
				filters: { selectedFilters }, expandedCountryIds, periodSwitch
			} = getState().topCountriesList;

			const leadsFrom = getFormValues('conversionsFromTopCountriesForm')(getState()) || {};

			if (expandedCountryIds.includes(country_id)) {
				return dispatch({ type: REMOVE_EXTENDED_DATA, payload: country_id });
			}

			if (!expandedCountryIds[0] && !country_id) {
				return;
			}

			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;

			const query = qs.stringify({
				filters,
				period: periodSwitch,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				expand_by_country_ids: country_id || expandedCountryIds,
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});

			if (country_id) {
				dispatch({ type: SET_LOADING_COUNTRY_ID, payload: country_id });
			}

			return (http({
				url: `v2/top-countries/offers-expanded?${query}`,
				method: 'get',
				actions: [ FETCH_EXTENDED_DATA_SUCCESS, FETCH_DATA_ERROR ],
				addToResponse: { country_id: country_id || null }
			}))(dispatch)
				.then(action => {

					if (action.type === FETCH_EXTENDED_DATA_SUCCESS && country_id) {
						dispatch({ type: SET_LOADING_COUNTRY_ID, payload: null });
					}
				});
		},
		fetchExtendedAffiliateData: (country_id, offer_id) => (dispatch, getState) => {
			const {
				filters: { selectedFilters }, periodSwitch, expandedCountryOfferIds
			} = getState().topCountriesList;

			const leadsFrom = getFormValues('conversionsFromTopCountriesForm')(getState()) || {};

			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;

			if (country_id && offer_id) {
				dispatch({ type: SET_LOADING_OFFER_COUNTRY_ID, payload: { country_id, offer_id } });
			}
			if (_find(expandedCountryOfferIds, { offer_id, country_id })) {
				return dispatch({ type: REMOVE_EXTENDED_OFFER_DATA, payload:  { offer_id, country_id } });
			}

			const query = qs.stringify({
				filters,
				period: periodSwitch,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				expand_by_offers: [{ country_id, offer_id }],
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});

			dispatch({ type: START_LOAD_AFFILIATE_DATA, payload: { country_id, offer_id } });
			return (http({
				url: `v2/top-countries/affiliates-expanded?${query}`,
				method: 'get',
				actions: [ FETCH_AFFILIATES_EXTENDED_DATA_SUCCESS, FETCH_DATA_ERROR ],
				addToResponse: { country_id, offer_id }
			}))(dispatch)
				.then(action => {

					if (action.type === FETCH_AFFILIATES_EXTENDED_DATA_SUCCESS) {
						dispatch({ type: SET_LOADING_OFFER_COUNTRY_ID, payload: null });
					}
				});
		},
		updateConversionFromCheckbox: (name = '') => (dispatch, getState) => {
			const formName = 'conversionsFromTopCountriesForm';

			if (name === 'is_from_cpa') {
				dispatch(change(formName, 'is_from_real_crm', false));
			}

			if (name === 'is_from_real_crm') {
				dispatch(change(formName, 'is_from_cpa', false));
			}
		},
		apply: () => dispatch => {
			dispatch({ type: DEFAULT_PAGINATION });
			dispatch(actions.fetchData());
		},
		togglePeriodSwitch: (type) => (dispatch) => {
			dispatch({ type: TOGGLE_PERIOD_SWITCH, payload: type || null });
			dispatch(actions.apply());
		},
		gotoPage: page => (dispatch, getState) => {
			dispatch({ type: PAGINATE, payload: page });
			dispatch(actions.fetchData());
		}
	},
	filters.actions,
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return {...initialState};
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.data,
			pagination: {
				...state.pagination,
				total: _get(action, 'payload.data.data[1].rows_count', 0)
			},
			intervals: action.payload.data.intervals,
			showProgressBar: false,
			error: false
		}),
		[FETCH_NOTES_DATA_SUCCESS]: (state, action) => ({
			...state,
			noteData: action.payload.data
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state, showProgressBar: !state.showProgressBar
		}),
		[TOGGLE_NOTE_POPUP]: (state, action) => ({
			...state,
			isNotePopupVisible: !state.isNotePopupVisible,
			notePopupData: action.payload ? action.payload : null
		}),
		[FETCH_EXTENDED_DATA_SUCCESS]: (state, action) => ({
			...state,

			extendedOfferData: action.meta.country_id
				? [...state.extendedOfferData].concat(action.payload.data.data)
				: action.payload.data.data,
			expandedCountryIds: [...state.expandedCountryIds].concat(action.meta.country_id || [])
		}),
		[FETCH_AFFILIATES_EXTENDED_DATA_SUCCESS]: (state, action) => ({
			...state,
			loadingAffiliateDataOnOffer: null,
			extendedAffiliateData: action.meta.country_id
				? [...state.extendedAffiliateData].concat(action.payload.data.data)
				: action.payload.data.data,
			expandedCountryOfferIds: [...state.expandedCountryOfferIds].concat(action.meta || [])
		}),
		[START_LOAD_AFFILIATE_DATA]: (state, action) => ({
			...state, loadingAffiliateDataOnOffer: action.payload
		}),
		[TOGGLE_PERIOD_SWITCH]: (state, action) => ({
			...state, periodSwitch: action.payload
		}),
		[REMOVE_EXTENDED_DATA]: (state, action) => ({
			...state,
			extendedOfferData: state.extendedOfferData.filter(ed => action.payload !== ed['country_id']),
			expandedCountryIds: state.expandedCountryIds.filter(eai => action.payload !== eai)
		}),
		[REMOVE_EXTENDED_OFFER_DATA]: (state, action) => ({
			...state,
			extendedAffiliateData: state.extendedAffiliateData.filter(
				ed => !(action.payload.country_id === ed['country_id'] && action.payload.offer_id === ed['offer_id'])
			),
			expandedCountryOfferIds: state.expandedCountryOfferIds.filter(
				eai => !(action.payload.offer_id === eai.offer_id && action.payload.country_id === eai.country_id)
			)
		}),
		[DEFAULT_PAGINATION]: (state, action) => ({ ...state, pagination: initialState.pagination }),
		[SET_LOADING_COUNTRY_ID]: (state, action) => ({ ...state, loadingAffiliateId: action.payload }),
		[SET_LOADING_OFFER_COUNTRY_ID]: (state, action) => ({
			...state, loadingOfferCountryId: action.payload
		}),
		[PAGINATE]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state, showProgressBar: false, error: true
		})
	},
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, {...initialState});

// selectors
export const selectors = {
	getData: state => state.topCountriesList.data,
	getExpandedCountryIds: state => state.topCountriesList.expandedCountryIds,
	getLoadingOfferCountryId: state => state.topCountriesList.loadingOfferCountryId,
	getIntl: state => intl(state),
	getTable: state => {
		const { table, data, intervals, extendedOfferData, extendedAffiliateData } = state.topCountriesList;
		const rows = [];

		for (const geoRow of data || []) {
			const expandedOfferRows = extendedOfferData
				.filter(item => item.country_id === geoRow.country_id);
			rows.push(geoRow);
			for (const offerRow of expandedOfferRows) {
				rows.push(offerRow);
				const expandedAffiliateRows = extendedAffiliateData
					.filter(item => item.country_id === offerRow.country_id && item.offer_id === offerRow.offer_id);
				rows.push(...expandedAffiliateRows);
			}
		}

		return {
			headers: Object.values(table.columns),
			rows: rows.filter(Boolean),
			intervals: intervals && intervals.map(item => item.date).sort(() => -1),
			totalRow: rows[0] && !rows[0].country_id ? rows[0] : null,
			rows_count: rows[1] && rows[1].rows_count
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.topCountriesList.filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.topCountriesList.calendar, state => state
	)(state),
	getPagination: state => {
		const { perPage, total, currentPage } = state.topCountriesList.pagination;
		return {
			...state.topCountriesList.pagination,
			pages: _range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getProgressBar: state => state.topCountriesList.showProgressBar,
	getLoadingAffiliateDataOnOffer: state => state.topCountriesList.loadingAffiliateDataOnOffer,
	getIsHiddenProfit: state => _get(getFormValues('conversionsFromTopCountriesForm')(state), 'is_hidden_profit'),
	getLoadingAffiliateId: state => state.topCountriesList.loadingAffiliateId,
	getExpandedCountryOfferIds: state => state.topCountriesList.expandedCountryOfferIds,
	getPeriodSwitch: state => state.topCountriesList.periodSwitch,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.topCountriesList.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/top-countries/index.js